/** Markdown-lite for the text tile (SEN-1419 deferred it "until the agent
 *  writes it" — the agent's first dashboard opened with a literal
 *  `## Site page`; design review 2026-09-02, F1).
 *
 *  Deliberately small: headings (# ## ###), paragraphs, `-`/`*` bullets,
 *  **bold**, *italic*, `code`. Everything is built as React nodes — never an
 *  HTML string — so agent- or user-written text cannot inject markup. What
 *  the parser does not know stays visible as the text it was. */
import { Fragment, type ReactNode } from 'react'

const HEADING_CLASS: Record<number, string> = {
  1: 'text-xl font-semibold leading-tight tracking-tight text-foreground',
  2: 'text-base font-semibold leading-snug text-foreground',
  3: 'text-sm font-semibold leading-snug text-foreground',
}

/** Inline spans: **bold**, *italic*, `code`. Left-to-right, no nesting. */
export function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = []
  // Emphasis needs a non-space right inside the markers: "5 * 3 * 2" is
  // arithmetic, "*word*" is italic (cold read 22).
  const re = /(\*\*(?=\S)[^*]+?(?<=\S)\*\*|\*(?=\S)[^*]+?(?<=\S)\*|`[^`]+`)/g
  let last = 0
  let m: RegExpExecArray | null
  let k = 0
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const tok = m[0]
    if (tok.startsWith('**')) out.push(<strong key={k++}>{tok.slice(2, -2)}</strong>)
    else if (tok.startsWith('`')) out.push(<code key={k++} className="rounded-sm bg-muted px-1 font-mono text-[0.9em]">{tok.slice(1, -1)}</code>)
    else out.push(<em key={k++}>{tok.slice(1, -1)}</em>)
    last = m.index + tok.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/** Task 10 fix round 1 (R6, task-10-review.md Minor #5): a title-less text
 *  tile's caption/aria-label falls back to its OWN first markdown heading
 *  (`#`/`##`/`###`) instead of the literal word "Text" — five differently-
 *  purposed tiles (the four section headers + the commentary callout) were
 *  otherwise announcing as identically-named "Text" regions to a screen
 *  reader, though a sighted reader sees four distinct section names. Falls
 *  back to `fallback` only when the text carries no heading at all. */
export function firstHeadingOrFallback(text: string, fallback: string): string {
  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const m = /^#{1,3}\s+(.*)$/.exec(raw.trimEnd())
    if (m) return m[1]!.trim() || fallback
  }
  return fallback
}

/** Blocks: a blank line separates paragraphs; consecutive bullet lines form
 *  one list; a heading is its own block. Single line breaks inside a
 *  paragraph are kept (the header tile writes "name\nfacts"). */
export function renderMarkdownLite(text: string): ReactNode {
  const lines = text.replace(/\r\n?/g, '\n').split('\n')
  const blocks: ReactNode[] = []
  let para: string[] = []
  let list: string[] = []
  let k = 0
  const flushPara = () => {
    if (para.length === 0) return
    blocks.push(
      <p key={k++} className="text-sm leading-snug text-foreground">
        {para.map((l, i) => (
          <Fragment key={i}>{i > 0 && <br />}{renderInline(l)}</Fragment>
        ))}
      </p>,
    )
    para = []
  }
  const flushList = () => {
    if (list.length === 0) return
    blocks.push(
      <ul key={k++} className="list-disc pl-4 text-sm leading-snug text-foreground">
        {list.map((l, i) => <li key={i}>{renderInline(l)}</li>)}
      </ul>,
    )
    list = []
  }
  for (const raw of lines) {
    const line = raw.trimEnd()
    const h = /^(#{1,3})\s+(.*)$/.exec(line)
    const b = /^[-*]\s+(.*)$/.exec(line)
    if (h) {
      flushPara(); flushList()
      const level = h[1]!.length
      const cls = HEADING_CLASS[level]!
      const body = renderInline(h[2]!)
      blocks.push(level === 1 ? <h1 key={k++} className={cls}>{body}</h1>
        : level === 2 ? <h2 key={k++} className={cls}>{body}</h2>
        : <h3 key={k++} className={cls}>{body}</h3>)
    } else if (b) {
      flushPara()
      list.push(b[1]!)
    } else if (line.trim() === '') {
      flushPara(); flushList()
    } else {
      flushList()
      para.push(line)
    }
  }
  flushPara(); flushList()
  return <div className="flex flex-col gap-1.5 break-words">{blocks}</div>
}
